import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useState, useEffect } from "react";
import { Tag, Plus, Trash2, Edit, Save, X, Loader2 } from "lucide-react";
import { ProductCategory as LocalCategory } from "@/types/product";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface CreateCategoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  categories: LocalCategory[];
  slug: string;
  onSave?: (category: any) => void;
  onUpdate?: (category: any) => void;
  onDelete?: (categoryId: string) => void;
}

export function CreateCategoryModal({
  isOpen,
  onClose,
  categories: initialCategories,
  slug,
  onSave,
  onUpdate,
  onDelete,
}: CreateCategoryModalProps) {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: "",
    color: "#64748b",
  });
  const [editingCategory, setEditingCategory] = useState<LocalCategory | null>(
    null,
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [localCategories, setLocalCategories] = useState<LocalCategory[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [categoryToDelete, setCategoryToDelete] =
    useState<LocalCategory | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadCategories();
    }
  }, [isOpen, initialCategories]);

  const loadCategories = async () => {
    setIsFetching(true);
    try {
      const filteredCategories = initialCategories.filter(
        (cat) => cat.id !== "",
      );
      setLocalCategories(filteredCategories);
    } catch (error) {
    } finally {
      setIsFetching(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      color: "#64748b",
    });
    setEditingCategory(null);
    setErrors({});
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleChange = (field: keyof typeof formData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = "El nombre es obligatorio";
    } else if (formData.name.trim().length < 2) {
      newErrors.name = "El nombre debe tener al menos 2 caracteres";
    }

    const isDuplicate = localCategories.some(
      (category) =>
        category.name.toLowerCase() === formData.name.toLowerCase() &&
        category.id !== editingCategory?.id,
    );

    if (isDuplicate) {
      newErrors.name = "Ya existe una categoría con este nombre";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setIsLoading(true);
    try {
      const categoryData = {
        id: editingCategory?.id || Date.now().toString(),
        name: formData.name,
        description: "",
        color: formData.color,
        createdAt: editingCategory?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      if (editingCategory) {
        if (onUpdate) {
          await onUpdate(categoryData);
        }

        toast({
          title: "Categoría actualizada",
          description: `La categoría "${formData.name}" ha sido actualizada`,
        });
      } else {
        if (onSave) {
          await onSave(categoryData);
        }

        toast({
          title: "Categoría creada",
          description: `La categoría "${formData.name}" ha sido creada`,
        });
      }

      resetForm();
    } catch (error) {
      toast({
        title: "Error",
        description: `No se pudo ${
          editingCategory ? "actualizar" : "crear"
        } la categoría`,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (category: LocalCategory) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      color: category.color || "#64748b",
    });
  };

  const handleCancelEdit = () => {
    resetForm();
  };

  const handleDeleteClick = (category: LocalCategory) => {
    setCategoryToDelete(category);
  };

  const handleConfirmDelete = async () => {
    if (!categoryToDelete) return;
    const category = categoryToDelete;

    try {
      if (onDelete && category.id) {
        await onDelete(category.id);
      }

      toast({
        title: "Categoría eliminada",
        description: `La categoría "${category.name}" ha sido eliminada`,
      });

      if (editingCategory?.id === category.id) {
        resetForm();
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo eliminar la categoría",
        variant: "destructive",
      });
    } finally {
      setCategoryToDelete(null);
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center text-2xl">
              <Tag className="h-6 w-6 mr-2 text-purple-600" />
              {editingCategory ? "Editar Categoría" : "Gestión de Categorías"}
            </DialogTitle>
          </DialogHeader>

          {isFetching ? (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              <span className="ml-2">Cargando categorías...</span>
            </div>
          ) : (
            <div className="space-y-6">
              <form
                onSubmit={handleSubmit}
                className="bg-slate-50 p-4 rounded-lg border border-slate-200 space-y-3"
              >
                <div className="flex items-center justify-between">
                  <Label
                    htmlFor="name"
                    className="text-sm font-semibold text-slate-700"
                  >
                    {editingCategory ? "Editar Categoría" : "Nueva Categoría"}
                  </Label>
                  {editingCategory && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleCancelEdit}
                      className="h-6 px-2 text-xs text-slate-500 hover:text-slate-700"
                    >
                      Cancelar edición
                    </Button>
                  )}
                </div>

                <div className="flex gap-3 items-start">
                  <div className="flex-1">
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => handleChange("name", e.target.value)}
                      placeholder="Ej: Software, Hardware, Servicios..."
                      className="bg-white"
                      disabled={isLoading}
                    />
                    {errors.name && (
                      <p className="text-red-500 text-xs mt-1 ml-1">
                        {errors.name}
                      </p>
                    )}
                  </div>
                  <Button type="submit" disabled={isLoading}>
                    {isLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : editingCategory ? (
                      <Save className="h-4 w-4" />
                    ) : (
                      <Plus className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </form>

              <div>
                <h3 className="font-semibold text-lg mb-4">
                  Categorías Existentes ({localCategories.length})
                </h3>

                {localCategories.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Tag className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No hay categorías creadas</p>
                    <p className="text-sm mt-1">
                      Crea tu primera categoría usando el formulario anterior
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {localCategories.map((category) => (
                      <Card
                        key={category.id}
                        className={`p-4 hover:shadow-md transition-shadow ${
                          editingCategory?.id === category.id
                            ? "ring-2 ring-blue-500"
                            : ""
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3 flex-1">
                            <div className="h-10 w-10 rounded-md flex-shrink-0 flex items-center justify-center bg-slate-100 border border-slate-200">
                              <Tag className="h-5 w-5 text-slate-500" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <h4 className="font-semibold text-sm truncate">
                                  {category.name}
                                </h4>
                                {category.id.startsWith("temp-") && (
                                  <span className="text-xs px-2 py-0.5 bg-yellow-100 text-yellow-800 rounded">
                                    Pendiente
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-slate-400 mt-1">
                                Creada:{" "}
                                {new Date(
                                  category.createdAt,
                                ).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-1 ml-2">
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8"
                              onClick={() => handleEdit(category)}
                              disabled={
                                editingCategory?.id === category.id || isLoading
                              }
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDeleteClick(category)}
                              disabled={
                                editingCategory?.id === category.id || isLoading
                              }
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!categoryToDelete}
        onOpenChange={(open) => !open && setCategoryToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Esto eliminará permanentemente
              la categoría "{categoryToDelete?.name}".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
